import { useState, useMemo } from 'react';
import ImageUpload from '../ImageUpload/ImageUpload';
import ModalCloseButton from './ModalCloseButton';

const EditClubModal = ({ club, onClose, onSave, onDelete, userRole }) => {
  const [clubName, setClubName] = useState(club?.name || '');
  const [clubPhotoUrl, setClubPhotoUrl] = useState(club?.photoUrl || null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [typedCode, setTypedCode] = useState('');

  const isOwner = userRole === 'owner';

  // Generate a random 4-digit security code for club deletion
  const securityCode = useMemo(() => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  }, []);

  const handleSave = () => {
    onSave(club.id, clubName.trim(), clubPhotoUrl);
    onClose();
  };

  const displayImageUrl = clubPhotoUrl || 
    `https://placehold.co/128x128/e5e7eb/6b7280?text=${(clubName || 'CL').slice(0, 2).toUpperCase()}`;

  const isCodeCorrect = typedCode.trim() === securityCode;

  return (
    <div className="modal-overlay">
      <div className="modal">
        {/* Subtle Delete Club icon top-left for Owner */}
        {isOwner && !showConfirmDelete && (
          <button
            onClick={() => setShowConfirmDelete(true)}
            className="modal-delete"
            title="Delete Club"
            aria-label="Delete Club"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </button>
        )}

        <ModalCloseButton onClick={onClose} />

        {showConfirmDelete ? (
          /* CONFIRM DELETE CLUB VIEW */
          <div>
            <h3 className="modal-title">
              Are you sure?
            </h3>

            <div className="modal-body">
              <p className="modal-text">
                This will delete <strong>{club.name}</strong> and all associated sessions, history, and player stats.
              </p>
              <p className="modal-text modal-text-strong">
                Once confirmed this can't be undone.
              </p>

              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="caption text-slate-300 mb-3">
                  Enter security code <strong className="modal-code">{securityCode}</strong> to confirm:
                </p>
                <input
                  type="text"
                  maxLength={4}
                  placeholder="Enter 4-digit code"
                  value={typedCode}
                  onChange={(e) => setTypedCode(e.target.value)}
                  className="input input-code"
                  autoFocus
                />
              </div>
            </div>

            <div className="modal-footer">

              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmDelete(false)}
              >
                <span className="btn-text">CANCEL</span>
              </button>
              <button
                type="button"
                disabled={!isCodeCorrect}
                className="btn btn-danger"
                onClick={() => {
                  if (onDelete) onDelete(club.id);
                  onClose();
                }}
              >
                <span className="btn-text">DELETE</span>
              </button>
            </div>
          </div>
        ) : (
          /* EDIT CLUB FORM */
          <>
            <h3 className="modal-title">Edit Club</h3>
            <p className="caption text-center mb-6">
              Join Code: <span className="font-bold text-[#f5b83d] text-base ml-1">{club.joinCode}</span>
            </p>

            <div className="modal-content">
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Club Name"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  className="input text-center"
                />
                
                <div className="flex flex-col items-center gap-md">
                  <ImageUpload
                    currentImageUrl={displayImageUrl}
                    onImageChange={setClubPhotoUrl}
                    type="club"
                    id={club.id}
                    aspect={1}
                    circularCrop={true}
                  />
                </div>
              </div>
            </div>

            <button
              onClick={handleSave}
              disabled={!clubName.trim()}
              className="btn btn-primary btn-full mt-lg"
            >
              <span className="btn-text">
                SAVE CHANGES
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default EditClubModal;
